import { Close } from '@mui/icons-material';
import { startCase } from 'lodash';
import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Text,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { InsightsTimeOptions } from 'src/graphql';
import { useInsightsV1, useIsFeatureEnabled } from 'src/hooks';
import { useTheme } from 'styled-components';
import { FeatureFlag } from 'src/types';
import {
  handleClickAll,
  handleClickPastMonth,
  renderActiveShape,
  sortTooltips,
  updateYear,
} from './InsightsDashboard.helpers';
import {
  ChartContainer,
  Expand,
  InsightsContainer,
  InsightsHeading,
  InsightsMenuItem,
  InsightsRow,
  InsightsTitle,
  InsightsToggle,
  InsightsToggleContainer,
  TotalNumberOfReports,
} from './InsightsDashboard.styles';

export const InsightsDashboard = () => {
  const {
    activeIndex,
    setActiveIndex,
    totalNumberOfreports,
    isInUse,
    setIsInUse,
    years,
    currentYear,
    setCurrentYear,
    open,
    setOpen,
    dataForAreaChart,
    dataForPieChart,
    dataForRadarChart,
    dataForBarChart,
  } = useInsightsV1();

  const AngledAxisTick = ({
    x,
    y,
    payload,
  }: {
    x: number;
    y: number;
    payload: { value: string };
  }) => (
    <Text x={x} y={y} width={230} fontSize={13} angle={-30} textAnchor="end">
      {payload.value}
    </Text>
  );

  const theme = useTheme();

  const statusColours = {
    NEW: theme.isVaultVariant
      ? theme.newPalette.chart.blue
      : theme.atlas.semantic.color.dataVisualization.chart.blue,
    READ: theme.isVaultVariant
      ? theme.newPalette.turquoise.T300
      : theme.atlas.semantic.color.action.primary.default,
    INVESTIGATING: theme.isVaultVariant
      ? theme.newPalette.yellow.Y300
      : theme.atlas.semantic.color.status.warning.default,
    CLOSED: theme.isVaultVariant
      ? theme.newPalette.neutral.N600
      : theme.atlas.semantic.color.type.muted,
  };

  const isVaultTalkEnabled = useIsFeatureEnabled(FeatureFlag.VAULT_TALK);
  const isSlackEnabled = useIsFeatureEnabled(FeatureFlag.SLACK_REPORTING);
  const isCaseImporterEnabled = useIsFeatureEnabled(FeatureFlag.CASE_IMPORTER);

  return (
    <InsightsContainer data-testid={'insights-container'}>
      <InsightsToggleContainer>
        <InsightsTitle>Currently showing</InsightsTitle>
        <InsightsToggle
          onClick={() => setOpen((currentVal) => !currentVal)}
          open={open}
          defaultValue={1}
          variant="standard"
          MenuProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            MenuListProps: {
              style: {
                border: `1px solid ${theme.isVaultVariant ? theme.newPalette.turquoise.T300 : theme.atlas.semantic.color.action.primary.default}`,
                borderRadius: '0px 0px 5px 5px',
              },
            },
          }}
          IconComponent={() =>
            open ? (
              <Close
                onClick={() => () => setOpen((currentVal) => !currentVal)}
              />
            ) : (
              <Expand onClick={() => setOpen((currentVal) => !currentVal)} />
            )
          }
        >
          <InsightsMenuItem
            value={1}
            onClick={(e: React.MouseEvent) =>
              handleClickAll(e, setIsInUse, setOpen)
            }
            disabled={isInUse === InsightsTimeOptions.All}
          >
            All Time
          </InsightsMenuItem>
          <InsightsMenuItem
            value={2}
            onClick={(e: React.MouseEvent) =>
              handleClickPastMonth(e, setIsInUse, setOpen)
            }
            disabled={isInUse === InsightsTimeOptions.Month}
          >
            Last Month
          </InsightsMenuItem>

          {years?.reportYears.map((year, idx) => (
            <InsightsMenuItem
              value={3 + idx}
              key={year}
              onClick={(e: React.MouseEvent) =>
                updateYear(e, year, setIsInUse, setOpen, setCurrentYear)
              }
              disabled={
                currentYear === year && isInUse === InsightsTimeOptions.Year
              }
            >
              {year}
            </InsightsMenuItem>
          ))}
        </InsightsToggle>
      </InsightsToggleContainer>

      <InsightsRow>
        <ChartContainer $screenWidth="full" key={currentYear ?? 0}>
          <InsightsHeading>Number of Reports</InsightsHeading>
          <ResponsiveContainer width="95%" height="80%">
            <AreaChart data={dataForAreaChart?.monthlyReportsBySource}>
              <XAxis dataKey="month" />
              <YAxis />
              <Legend iconType="rect" iconSize={18} />
              <Tooltip itemSorter={sortTooltips} />
              <Area
                type="monotone"
                dataKey={(val) => val['app']}
                name="Mobile App Reporting"
                fillOpacity="0.7"
                stroke="#19142f"
                fill="#19142f"
              />
              <Area
                type="monotone"
                dataKey={(val) => val['openReporting']}
                name="Open Reporting"
                fillOpacity="0.7"
                stroke="#196FA0"
                fill="#196FA0"
              />
              <Area
                type="monotone"
                dataKey={(val) => val['manualCaseCreation']}
                name="Manual Intake"
                fillOpacity="0.7"
                stroke="#55ADBE"
                fill="#55ADBE"
              />
              {isVaultTalkEnabled && (
                <Area
                  type="monotone"
                  dataKey={(val) => val['vaultTalk']}
                  name="VaultTalk"
                  fillOpacity="0.7"
                  stroke="#B4CCD0"
                  fill="#B4CCD0"
                />
              )}
              {isSlackEnabled && (
                <Area
                  type="monotone"
                  dataKey={(val) => val['slack']}
                  name="Slack Intake"
                  fillOpacity="0.7"
                  stroke={
                    theme.isVaultVariant
                      ? theme.newPalette.chart.green
                      : theme.atlas.semantic.color.dataVisualization.chart.green
                  }
                  fill={
                    theme.isVaultVariant
                      ? theme.newPalette.chart.green
                      : theme.atlas.semantic.color.dataVisualization.chart.green
                  }
                />
              )}
              {isCaseImporterEnabled && (
                <Area
                  type="monotone"
                  dataKey={(val) => val['imported']}
                  name="Imported"
                  fillOpacity="0.7"
                  stroke={
                    theme.isVaultVariant
                      ? theme.newPalette.chart.violet
                      : theme.atlas.semantic.color.dataVisualization.chart
                          .violet
                  }
                  fill={
                    theme.isVaultVariant
                      ? theme.newPalette.chart.violet
                      : theme.atlas.semantic.color.dataVisualization.chart
                          .violet
                  }
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </ChartContainer>
      </InsightsRow>

      <InsightsRow multipleCharts>
        <ChartContainer $screenWidth="half">
          <InsightsHeading>Resolution Status</InsightsHeading>
          <ResponsiveContainer width="100%" height="80%">
            <PieChart width={600} height={500}>
              <Pie
                activeIndex={activeIndex}
                activeShape={renderActiveShape}
                data={dataForPieChart?.reportsByStatus.map((entry) => ({
                  ...entry,
                  name: startCase(entry.status.toLowerCase()),
                }))}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                fill="#19142f"
                dataKey="count"
                onMouseEnter={(_data, index) => setActiveIndex(index)}
              >
                {dataForPieChart?.reportsByStatus.map((entry) => (
                  <Cell key={entry.status} fill={statusColours[entry.status]} />
                ))}
              </Pie>
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartContainer>

        <ChartContainer $screenWidth="half">
          <InsightsHeading>Department Radar for Issues</InsightsHeading>
          <ResponsiveContainer width="100%" height="80%">
            <RadarChart
              cx="50%"
              cy="50%"
              outerRadius="70%"
              width={300}
              height={500}
              data={dataForRadarChart?.reportsByDepartment}
            >
              <PolarGrid />
              <PolarAngleAxis dataKey="department" fontSize={12} />
              <PolarRadiusAxis />
              <Radar
                dataKey="count"
                stroke="#368493"
                fill="#368493"
                fillOpacity={0.7}
              />
              <Tooltip />
            </RadarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </InsightsRow>

      <InsightsRow>
        <ChartContainer $screenWidth="full">
          <InsightsHeading>Category</InsightsHeading>
          <ResponsiveContainer width="100%" height="85%">
            <BarChart
              width={1260}
              height={400}
              data={dataForBarChart?.reportsByCategory}
              margin={{ top: 20, right: 70, left: 70, bottom: 50 }}
            >
              <XAxis
                dataKey="category"
                interval={0}
                textAnchor="end"
                height={90}
                padding={{ left: 10, right: 10 }}
                tickMargin={17}
                tick={(tickProps) => <AngledAxisTick {...tickProps} />}
              />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="count"
                name="Number of Reports"
                fill="#368493"
                fillOpacity="0.7"
                stroke="#368493"
              />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </InsightsRow>

      <TotalNumberOfReports data-testid={'total-reports'}>
        Reports Filed: {totalNumberOfreports}
      </TotalNumberOfReports>
    </InsightsContainer>
  );
};
